import { Activity, CheckCircle2, ListChecks, TrendingUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { statusLabel } from '@/lib/statusLabel'
import { cn } from '@/lib/utils'
import { qualityGateTone, type ChapterQualitySnapshot } from './qualityGate'

interface ChapterQualityCardProps {
  quality?: ChapterQualitySnapshot | null
  history?: ChapterQualitySnapshot[]
  loading?: boolean
  compact?: boolean
}

function TrendBars({ history }: { history: ChapterQualitySnapshot[] }) {
  const recent = history.slice(-8)
  if (recent.length === 0) return null
  return (
    <div className="flex h-12 items-end gap-1" aria-hidden="true">
      {recent.map((item) => (
        <div
          key={item.id}
          className="w-3 rounded-t-sm bg-primary/70"
          style={{ height: `${Math.max(10, item.score)}%` }}
          title={`${item.score}`}
        />
      ))}
    </div>
  )
}

export function ChapterQualityCard({ quality, history = [], loading, compact }: ChapterQualityCardProps) {
  const { t } = useTranslation()
  if (!quality) {
    return (
      <div className="rounded-md border border-dashed bg-background/45 p-3 text-sm text-muted-foreground">
        {loading ? t('qualityGate.loading') : t('qualityGate.noHistory')}
      </div>
    )
  }

  const tone = qualityGateTone(quality.score)
  const qualityStatus = statusLabel(quality.status, 'quality')
  const dimensions = quality.dimensions.length ? quality.dimensions : [{
    key: 'overall',
    label: t('qualityGate.overall'),
    score: quality.score,
    grade: quality.grade,
    status: quality.status ?? 'unknown',
    layer: quality.layer ?? '',
  }]
  const suggestions = quality.suggestions.slice(0, compact ? 2 : 5)

  return (
    <div className="rounded-md border bg-background/55 p-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Activity className="size-4 text-primary" />
            {t('qualityGate.cardTitle')}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-2xl font-semibold tabular-nums">{quality.score}</span>
            <Badge variant="outline" className="rounded-md" style={{ borderColor: tone.color, color: tone.color }}>
              {quality.grade ?? t(tone.labelKey)}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {t(qualityStatus.textKey, qualityStatus.textOptions)} {quality.layer ? `· ${quality.layer}` : ''}
            </span>
          </div>
        </div>
        {history.length > 1 ? (
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <TrendingUp className="size-4" />
            <TrendBars history={history} />
          </div>
        ) : null}
      </div>

      <div className="mt-3 space-y-2">
        {dimensions.map((dimension) => (
          <div key={dimension.key} className="grid gap-1">
            <div className="flex items-center justify-between gap-3 text-xs">
              <span className="truncate text-muted-foreground">{dimension.key === 'overall' ? t('qualityGate.overall') : dimension.label}</span>
              <span className="font-medium tabular-nums">{dimension.score}</span>
            </div>
            <Progress value={dimension.score} className="h-1.5" />
            {/* 维度自带的说明 (如章级字数的「实际 2264 / 目标 1500」) 此前从不渲染,
                维度行只剩一个没有解释的分数。overall 的 notes 与下方「建议」重复, 跳过。 */}
            {dimension.key !== 'overall' && dimension.notes?.length ? (
              <ul className="space-y-0.5 text-[11px] leading-snug text-muted-foreground">
                {dimension.notes.map((note, index) => (
                  <li key={`${dimension.key}-note-${index}`}>{note}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}
      </div>

      {suggestions.length ? (
        <div className="mt-3 border-t pt-3">
          <div className="mb-2 flex items-center gap-2 text-xs font-medium text-muted-foreground">
            <ListChecks className="size-3.5" />
            {t('qualityGate.suggestions')}
          </div>
          <ul className="space-y-1 text-xs text-muted-foreground">
            {suggestions.map((item, index) => (
              <li key={`${index}:${item}`} className="flex gap-2">
                <CheckCircle2 className={cn('mt-0.5 size-3 shrink-0', tone.band === 'failed' ? 'text-destructive' : 'text-primary')} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}
